/**
 * Deal form validation — pure functions for testability
 */

import { STAGE_META } from '../../constants/Stages.js';
import { applyDealRules } from '../../utils/FormLayoutRules.js';
import { RevenueCalculationService } from '../../services/RevenueCalculationService.js';
import { parseFrequency } from '../../utils/InvoiceFrequency.js';

export const FIELD_TABS = {
    dealName: 'details',
    dealDate: 'details',
    dealClientId: 'details',
    referralContactId: 'details',
    dealOwnerUserId: 'details',
    dealPipeline: 'details',
    dealStage: 'details',
    dealAmount: 'details',
    dealProbability: 'details',
    expectedCloseDate: 'details',
    dealLostReason: 'details',
    dealLostReasonCategory: 'details',
    dealNextStep: 'details',
    dealContactRoles: 'contacts',
    dealContractValue: 'revenue',
    dealPaymentTerms: 'revenue',
    dealPaymentModel: 'revenue',
    dealPaymentCount: 'revenue',
    sowSigningDate: 'revenue',
    dealBillingStartDate: 'revenue',
    dealBillingEndDate: 'revenue',
    dealInvoiceAmount: 'revenue',
    dealCurrency: 'revenue',
    dealMilestoneRows: 'revenue',
    dealCustomScheduleRows: 'revenue',
    dealCommissionEligible: 'commission',
    dealCommissionModel: 'commission',
    dealCommissionRate: 'commission',
    dealCommissionFixed: 'commission',
    dealCommissionSplit: 'commission'
};

const TAB_ORDER = ['details', 'revenue', 'contacts', 'commission', 'activities', 'commission-schedule'];

export function getFirstErrorTab(errors) {
    for (const tab of TAB_ORDER) {
        if (Object.keys(errors).some(fieldId => FIELD_TABS[fieldId] === tab)) {
            return tab;
        }
    }
    return 'details';
}

export function validateDealForm(state) {
    const errors = {};
    const warnings = {};
    const {
        dealName,
        clientId,
        ownerUserId,
        pipeline,
        stage,
        dealAmount,
        probability,
        expectedCloseDate,
        lostReason,
        lostReasonCategory,
        dealLostReasonCategory,
        nextStep,
        primaryContactId,
        clientContactIds = [],
        dealContactRoles = [],
        contractValue,
        paymentModel,
        paymentTerms,
        sowSigningDate,
        expectedFirstInvoiceDate,
        lastInvoiceDate,
        invoiceFrequency,
        invoiceAmount,
        totalInvoices,
        commissionEligible,
        dealType,
        serviceLine,
        useAccountCommissionDefaults,
        commissionSplits = [],
        dealMilestones = [],
        customScheduleRows = [],
        currency,
        taxIncluded
    } = state;

    const name = (dealName || '').trim();
    if (!name) {
        errors.dealName = 'Deal name is required.';
    } else if (name.length > 120) {
        errors.dealName = 'Deal name must be 120 characters or less.';
    }

    if (!clientId) errors.dealClientId = 'Account is required.';
    if (!ownerUserId) errors.dealOwnerUserId = 'Deal owner is required.';
    if (!pipeline) errors.dealPipeline = 'Pipeline is required.';
    if (!stage) errors.dealStage = 'Stage is required.';

    const amount = Number(dealAmount);
    if (!dealAmount && dealAmount !== 0) {
        errors.dealAmount = 'Deal amount is required.';
    } else if (Number.isNaN(amount) || amount <= 0) {
        errors.dealAmount = 'Deal amount must be greater than 0.';
    } else if (amount > 999_999_999_999) {
        errors.dealAmount = 'Deal amount is too large.';
    }

    if (probability !== '' && probability != null) {
        const prob = Number(probability);
        if (Number.isNaN(prob) || prob < 0 || prob > 100) {
            errors.dealProbability = 'Probability must be between 0 and 100.';
        } else if (stage && STAGE_META[stage]) {
            const meta = STAGE_META[stage];
            if (prob < meta.probabilityMin || prob > meta.probabilityMax) {
                warnings.dealProbability =
                    `Typical range for ${meta.label} is ${meta.probabilityMin}–${meta.probabilityMax}%.`;
            }
        }
    }

    if (expectedCloseDate && Number.isNaN(Date.parse(expectedCloseDate))) {
        errors.expectedCloseDate = 'Enter a valid expected close date.';
    }

    if (state.leadSource === 'referral') {
        if (!state.referralContactId) {
            errors.referralContactId = 'Referrer contact is required for referral deals.';
        }
    }

    if (stage === 'closedLost' && !(lostReason || '').trim()) {
        errors.dealLostReason = 'Lost reason is required for closed lost deals.';
    }
    if (stage === 'closedLost' && !(state.lostReasonCategory || state.dealLostReasonCategory || '').trim()) {
        errors.dealLostReasonCategory = 'Lost category is required for closed lost deals.';
    }

    const layout = applyDealRules({
        dealType: state.dealType,
        dealPipeline: pipeline,
        serviceLine: state.serviceLine,
        stage,
        dealContactRoles
    });
    if (layout.requiredFields.has('nextStep') && !(nextStep || '').trim()) {
        errors.dealNextStep = 'Next step is required at this stage.';
    }

    if (stage === 'closed') {
        const hasContactRole = dealContactRoles.some(r => r.contactId && r.role);
        if (!hasContactRole) {
            errors.dealContactRoles = 'Add at least one contact with a role.';
        }

        const cv = Number(contractValue);
        const model = (paymentModel || '').trim();

        if (model !== 'ongoing') {
            if (!contractValue && contractValue !== 0) {
                errors.dealContractValue = 'Contract value is required for closed won deals.';
            } else if (Number.isNaN(cv) || cv <= 0) {
                errors.dealContractValue = 'Contract value must be greater than 0.';
            }
        } else if (contractValue !== '' && contractValue != null && (Number.isNaN(cv) || cv < 0)) {
            errors.dealContractValue = 'Contract value must be zero or greater.';
        }

        if (!(paymentTerms || '').trim()) {
            errors.dealPaymentTerms = 'Payment terms are required for closed won deals.';
        }
        if (!model) {
            errors.dealPaymentModel = 'Payment model is required for closed won deals.';
        }
        if (['monthly', 'weekly', 'quarterly', 'annual'].includes(model)) {
            const count = Number(state.paymentCount ?? state.dealPaymentCount ?? state.totalInvoices);
            if (!Number.isFinite(count) || count < 1) {
                errors.dealPaymentCount = 'Number of payments is required for closed won deals.';
            }
        }
        const hasCloseDate = (expectedCloseDate || '').trim() && !Number.isNaN(Date.parse(expectedCloseDate));
        if (!hasCloseDate) {
            errors.expectedCloseDate = 'Expected close date is required for closed won deals.';
        }

        if (expectedFirstInvoiceDate && lastInvoiceDate &&
            new Date(expectedFirstInvoiceDate) > new Date(lastInvoiceDate)) {
            errors.dealBillingEndDate = 'Billing end date cannot be before the billing start date.';
        }

        const parsed = parseFrequency(invoiceFrequency);
        if (parsed.type === 'custom' && (!parsed.interval || parsed.interval < 1)) {
            errors.dealPaymentModel = 'Custom interval must be at least 1.';
        }

        if (model === 'milestone') {
            const v = RevenueCalculationService.validateMilestones({
                contractValue: cv,
                dealAmount: amount,
                dealMilestones
            });
            if (!v.valid) errors.dealMilestoneRows = v.message;
            else if (v.warning) warnings.dealMilestoneRows = v.warning;
        }
        if (model === 'custom') {
            const v = RevenueCalculationService.validateCustomSchedule({
                contractValue: cv,
                dealAmount: amount,
                customScheduleRows
            });
            if (!v.valid) errors.dealCustomScheduleRows = v.message;
            else if (v.warning) warnings.dealCustomScheduleRows = v.warning;
        }
        if (['monthly', 'weekly', 'quarterly', 'annual'].includes(model)) {
            const recurring = RevenueCalculationService.validateRecurringContract({
                paymentModel: model,
                contractValue: cv,
                dealAmount: amount,
                expectedFirstInvoiceDate,
                lastInvoiceDate,
                invoiceFrequency,
                invoiceAmount
            });
            if (!recurring.valid) {
                errors.dealContractValue = recurring.message;
            } else if (recurring.warning) {
                warnings.dealContractValue = recurring.warning;
            }
        }
        if (model === 'ongoing') {
            const ongoingStart = state.ongoingStartDate || expectedFirstInvoiceDate;
            if (!ongoingStart) {
                errors.dealOngoingStartDate = 'Expected start date is required for ongoing deals.';
            }
            if (state.retainerInputMode === 'percent') {
                const pct = Number(state.retainerPercent);
                if (pct > 100) {
                    errors.dealRetainerAmount = 'Retainer percentage cannot exceed 100%.';
                }
                if (pct > 0 && !(Number(state.dealAmount) > 0)) {
                    errors.dealAmount = 'Total deal amount is required when retainer is entered as a percentage.';
                }
            }
            const v = RevenueCalculationService.validateOngoingSchedule({
                ongoingScheduleRows: state.ongoingScheduleRows,
                customScheduleRows: state.customScheduleRows,
                retainerAmount: state.retainerAmount,
                estimatedAnnualValue: state.estimatedAnnualValue
            });
            if (!v.valid) errors.dealOngoingScheduleRows = v.message;
            else if (v.warning) warnings.dealOngoingScheduleRows = v.warning;
        }

        if (commissionEligible) {
            const splits = state.commissionSplits || commissionSplits;
            const total = splits.reduce((s, r) => s + (Number(r.splitPct) || 0), 0);
            const totalWarning = Math.abs(total - 100) > 0.01;
            const missingRecipient = splits.some((r) => !r.userId);
            if (totalWarning) {
                warnings.dealCommissionSplit = `Commission split totals ${total}%. It should total 100%.`;
            } else if (missingRecipient) {
                warnings.dealCommissionSplit = 'Each commission recipient should be a user.';
            }
        }
    }

    const layoutWarnings = applyDealRules({
        dealType: state.dealType,
        dealPipeline: pipeline,
        serviceLine: state.serviceLine,
        stage,
        dealContactRoles
    });
    layoutWarnings.warnings.forEach(w => {
        if (!warnings.dealContactRoles) warnings.dealContactRoles = w;
    });

    return {
        valid: Object.keys(errors).length === 0,
        errors,
        warnings,
        firstErrorTab: getFirstErrorTab(errors)
    };
}
